import { ICashManagementClient } from './ICashManagementClient';
import {
  CashMovementDto,
  CashBalanceDto,
  TillReconciliationDto,
  CashSummaryDto,
  CreateCashMovementRequest,
  CreateTillReconciliationRequest,
  CashMovementType,
} from './cashManagementTypes';

/**
 * Mocked implementation of Cash Management client with sample data
 */
export class CashManagementClientMocked implements ICashManagementClient {
  private movements: CashMovementDto[] = [];
  private reconciliations: TillReconciliationDto[] = [];
  private nextMovementId = 1;
  private nextReconciliationId = 1;

  constructor() {
    this.seedMockData();
  }

  private seedMockData(): void {
    const today = new Date();
    const storeNumber = '1002804';

    // Generate 25-30 varied recent movements for today
    const movementTypes: CashMovementType[] = ['Sale', 'FloatIn', 'FloatOut', 'CashIn', 'CashOut', 'Refund', 'BankDeposit', 'Withdrawal', 'Petty Cash'];
    const usernames = ['Sarah Manager', 'Alice Staff', 'Bob Staff', 'Charlie Staff', 'Diana Staff', 'Emma Staff', 'Frank Supervisor'];
    const tillNumbers = ['TILL-01', 'TILL-02', 'TILL-03', 'SAFE'];
    
    const numRecentMovements = 25 + Math.floor(Math.random() * 6); // 25-30 movements
    for (let i = 0; i < numRecentMovements; i++) {
      const hour = 8 + Math.floor(Math.random() * 12); // 8am to 8pm
      const minute = Math.floor(Math.random() * 60);
      const movementType: CashMovementType = movementTypes[Math.floor(Math.random() * movementTypes.length)];
      const tillNumber = tillNumbers[Math.floor(Math.random() * tillNumbers.length)];
      const username = usernames[Math.floor(Math.random() * usernames.length)];
      
      let amount: number;
      let reference: string | undefined;
      let notes: string | undefined;
      let transactionId: string | undefined;
      let documentId: number | undefined;

      switch (movementType) {
        case 'Sale':
          amount = 5 + Math.random() * 45; // £5 - £50
          transactionId = `TXN-${today.getTime()}-${i}`;
          documentId = i < 3 ? 1000 + i : undefined; // First 3 have CCTV
          break;
        case 'FloatIn':
          amount = 100 + Math.floor(Math.random() * 4) * 50; // £100, £150, £200, £250
          reference = `FLOAT-IN-${i}`;
          notes = 'Opening float or float top-up';
          break;
        case 'FloatOut':
          amount = 50 + Math.floor(Math.random() * 3) * 50; // £50, £100, £150
          reference = `FLOAT-OUT-${i}`;
          notes = 'Closing float removal';
          break;
        case 'CashIn':
          amount = 20 + Math.random() * 80; // £20 - £100
          reference = `CASHIN-${i}`;
          notes = 'Cash added from safe';
          break;
        case 'CashOut':
          amount = 20 + Math.random() * 80; // £20 - £100
          reference = `CASHOUT-${i}`;
          notes = 'Cash removed to safe';
          break;
        case 'Refund':
          amount = 10 + Math.random() * 40; // £10 - £50
          reference = `REFUND-${i}`;
          notes = ['Customer refund - faulty item', 'Wrong item refund', 'Customer changed mind'][Math.floor(Math.random() * 3)];
          transactionId = `REFUND-${today.getTime()}-${i}`;
          break;
        case 'BankDeposit':
          amount = 500 + Math.random() * 500; // £500 - £1000
          reference = `DEPOSIT-${today.toISOString().split('T')[0]}-${i}`;
          notes = 'Bank deposit';
          break;
        case 'Withdrawal':
          amount = 100 + Math.floor(Math.random() * 4) * 100; // £100, £200, £300, £400
          reference = `WITHDRAWAL-${i}`;
          notes = 'Cash withdrawal from safe';
          break;
        case 'Petty Cash':
          amount = 5 + Math.random() * 20; // £5 - £25
          reference = `PETTY-${i}`;
          notes = 'Petty cash expense';
          break;
        default:
          amount = 10;
      }

      this.movements.push({
        id: this.nextMovementId++,
        storeNumber,
        tillNumber: movementType === 'BankDeposit' ? undefined : tillNumber,
        movementType,
        amount: Math.round(amount * 100) / 100,
        currencyCode: 'GBP',
        timestamp: new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute).toISOString(),
        userId: `user${Math.floor(Math.random() * 7)}`,
        username,
        reference,
        notes,
        transactionId,
        documentId,
        createdAtUtc: new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute).toISOString(),
      });
    }

    // Generate mock movements for the last 7 days (reduced count)
    for (let dayOffset = 1; dayOffset < 7; dayOffset++) {
      const date = new Date(today);
      date.setDate(date.getDate() - dayOffset);

      // Morning opening float
      this.movements.push({
        id: this.nextMovementId++,
        storeNumber,
        tillNumber: 'TILL-01',
        movementType: 'FloatIn',
        amount: 250.00,
        currencyCode: 'GBP',
        timestamp: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 8, 0).toISOString(),
        userId: 'user123',
        username: 'Sarah Manager',
        reference: 'OPENING-FLOAT',
        notes: 'Opening float for Till 1',
        createdAtUtc: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 8, 0).toISOString(),
      });

      // Generate random sales throughout the day
      const numSales = 25 + Math.floor(Math.random() * 20);
      for (let i = 0; i < numSales; i++) {
        const hour = 9 + Math.floor(Math.random() * 11); // 9am to 8pm
        const minute = Math.floor(Math.random() * 60);
        const amount = 5 + Math.random() * 45; // £5 - £50

        this.movements.push({
          id: this.nextMovementId++,
          storeNumber,
          tillNumber: `TILL-0${1 + Math.floor(Math.random() * 3)}`,
          movementType: 'Sale',
          amount: Math.round(amount * 100) / 100,
          currencyCode: 'GBP',
          timestamp: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute).toISOString(),
          userId: `user${Math.floor(Math.random() * 5)}`,
          username: ['Alice Staff', 'Bob Staff', 'Charlie Staff', 'Diana Staff', 'Emma Staff'][Math.floor(Math.random() * 5)],
          transactionId: `TXN-${date.getTime()}-${i}`,
          documentId: dayOffset === 0 && i < 3 ? 1000 + i : undefined, // Only recent ones have CCTV
          createdAtUtc: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute).toISOString(),
        });
      }

      // A few refunds
      for (let i = 0; i < 2; i++) {
        const hour = 11 + Math.floor(Math.random() * 7);
        const minute = Math.floor(Math.random() * 60);
        this.movements.push({
          id: this.nextMovementId++,
          storeNumber,
          tillNumber: `TILL-0${1 + Math.floor(Math.random() * 2)}`,
          movementType: 'Refund',
          amount: 10 + Math.random() * 20,
          currencyCode: 'GBP',
          timestamp: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute).toISOString(),
          userId: 'user123',
          username: 'Sarah Manager',
          reference: `REFUND-${i}`,
          notes: 'Customer refund - faulty item',
          transactionId: `REFUND-${date.getTime()}-${i}`,
          createdAtUtc: new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute).toISOString(),
        });
      }

      // Bank deposit
      if (dayOffset < 3) {
        this.movements.push({
          id: this.nextMovementId++,
          storeNumber,
          movementType: 'BankDeposit',
          amount: 850 + Math.random() * 200,
          currencyCode: 'GBP',
          timestamp: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 15, 30).toISOString(),
          userId: 'user123',
          username: 'Sarah Manager',
          reference: `DEPOSIT-${date.toISOString().split('T')[0]}`,
          notes: 'Daily bank deposit',
          createdAtUtc: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 15, 30).toISOString(),
        });
      }

      // Evening till reconciliation
      if (dayOffset < 3) {
        this.reconciliations.push({
          id: this.nextReconciliationId++,
          storeNumber,
          tillNumber: 'TILL-01',
          reconciliationDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 20, 0).toISOString(),
          openingBalance: 250.00,
          closingBalance: 325.50 + (Math.random() * 20 - 10),
          expectedClosingBalance: 325.50,
          variance: Math.random() * 10 - 5,
          currencyCode: 'GBP',
          reconciledBy: 'Sarah Manager',
          status: Math.random() > 0.3 ? 'Reconciled' : 'Discrepancy',
          createdAtUtc: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 20, 0).toISOString(),
          cashBreakdown: {
            notes: { '50': 2, '20': 8, '10': 5, '5': 10 },
            coins: { '2': 5, '1': 10, '0.50': 8, '0.20': 15 },
            totalNotes: 260,
            totalCoins: 35.50,
          },
        });
      }
    }
  }

  async getCashMovements(storeNumber: string, startDate: string, endDate: string): Promise<CashMovementDto[]> {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Return all movements for mocking purposes (store number doesn't matter in dev)
    return this.movements.filter(m =>
      new Date(m.timestamp) >= start &&
      new Date(m.timestamp) <= end,
    ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  async getCashBalance(storeNumber: string): Promise<CashBalanceDto> {
    // Return all movements for mocking purposes (store number doesn't matter in dev)
    const movements = this.movements;
    const balance = movements.reduce((sum, m) => {
      if (['Sale', 'CashIn', 'FloatIn'].includes(m.movementType)) return sum + m.amount;
      return sum - m.amount;
    }, 0);

    return {
      storeNumber,
      currentBalance: Math.round(balance * 100) / 100,
      expectedBalance: Math.round(balance * 100) / 100 + 10,
      variance: 10,
      currencyCode: 'GBP',
      lastUpdatedUtc: new Date().toISOString(),
      tillBalances: [
        { tillNumber: 'TILL-01', balance: 325.50, status: 'Open', lastReconciliationUtc: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
        { tillNumber: 'TILL-02', balance: 289.30, status: 'Open', lastReconciliationUtc: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
        { tillNumber: 'TILL-03', balance: 412.75, status: 'Closed', lastReconciliationUtc: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString() },
      ],
    };
  }

  async recordCashMovement(movement: CreateCashMovementRequest): Promise<CashMovementDto> {
    const newMovement: CashMovementDto = {
      id: this.nextMovementId++,
      ...movement,
      currencyCode: 'GBP',
      timestamp: new Date().toISOString(),
      userId: 'current-user',
      username: 'Current User',
      createdAtUtc: new Date().toISOString(),
    };
    this.movements.push(newMovement);
    return newMovement;
  }

  async getTillReconciliations(storeNumber: string, startDate: string, endDate: string): Promise<TillReconciliationDto[]> {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return this.reconciliations.filter(r =>
      r.storeNumber === storeNumber &&
      new Date(r.reconciliationDate) >= start &&
      new Date(r.reconciliationDate) <= end,
    ).sort((a, b) => new Date(b.reconciliationDate).getTime() - new Date(a.reconciliationDate).getTime());
  }

  async createTillReconciliation(reconciliation: CreateTillReconciliationRequest): Promise<TillReconciliationDto> {
    const newRecon: TillReconciliationDto = {
      id: this.nextReconciliationId++,
      ...reconciliation,
      reconciliationDate: new Date().toISOString(),
      openingBalance: 250.00,
      expectedClosingBalance: reconciliation.closingBalance,
      variance: 0,
      currencyCode: 'GBP',
      reconciledBy: 'Current User',
      status: 'Reconciled',
      createdAtUtc: new Date().toISOString(),
    };
    this.reconciliations.push(newRecon);
    return newRecon;
  }

  async getCashSummary(storeNumber: string, date: string): Promise<CashSummaryDto> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const movements = await this.getCashMovements(storeNumber, startOfDay.toISOString(), endOfDay.toISOString());

    const totalSales = movements.filter(m => m.movementType === 'Sale').reduce((sum, m) => sum + m.amount, 0);
    const totalRefunds = movements.filter(m => m.movementType === 'Refund').reduce((sum, m) => sum + m.amount, 0);
    const totalCashIn = movements.filter(m => m.movementType === 'CashIn').reduce((sum, m) => sum + m.amount, 0);
    const totalCashOut = movements.filter(m => m.movementType === 'CashOut').reduce((sum, m) => sum + m.amount, 0);
    const bankDeposits = movements.filter(m => m.movementType === 'BankDeposit').reduce((sum, m) => sum + m.amount, 0);

    return {
      storeNumber,
      date,
      totalSales: Math.round(totalSales * 100) / 100,
      totalCashSales: Math.round(totalSales * 0.4 * 100) / 100,
      totalCardSales: Math.round(totalSales * 0.6 * 100) / 100,
      totalRefunds: Math.round(totalRefunds * 100) / 100,
      totalCashIn: Math.round(totalCashIn * 100) / 100,
      totalCashOut: Math.round(totalCashOut * 100) / 100,
      bankDeposits: Math.round(bankDeposits * 100) / 100,
      openingBalance: 250.00,
      closingBalance: 325.50,
      expectedBalance: 320.00,
      variance: 5.50,
      currencyCode: 'GBP',
      paymentMethodBreakdown: {
        Cash: Math.round(totalSales * 0.4 * 100) / 100,
        Card: Math.round(totalSales * 0.45 * 100) / 100,
        Contactless: Math.round(totalSales * 0.15 * 100) / 100,
      },
    };
  }
}
